// Cliente WebSocket da Arena (docs/02 §5):
// - hello/resume com lastSeq → recebe `state` completo
// - ping a cada 10 s → clock offset
// - reconexão com backoff 0,5 s → 8 s com jitter
// - fila de envio enquanto offline (só `answer`, com verificação de deadline)
import {
  PROTOCOL_VERSION,
  type ClientType,
  type Envelope,
  type HelloData,
  type Role,
  type ServerType,
} from "./protocol";

export type ConnStatus = "connecting" | "open" | "reconnecting" | "closed";

export interface ArenaWSOptions {
  role: Role;
  code: string;
  token?: string; // player_token ou stage_token (host usa cookie)
  onMessage: (type: ServerType, data: unknown, seq?: number) => void;
  onStatus?: (status: ConnStatus) => void;
}

const MAX_BACKOFF = 8000;
const INIT_BACKOFF = 500;
const PING_INTERVAL = 10_000;

export class ArenaWS {
  private ws: WebSocket | null = null;
  private lastSeq = 0;
  private backoff = INIT_BACKOFF;
  private stopped = false;
  private queue: { t: ClientType; d: unknown }[] = [];
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private noticeTimer: ReturnType<typeof setTimeout> | null = null;
  private status: ConnStatus = "closed";
  private clockOffset = 0; // serverTime - clientTime

  constructor(private opts: ArenaWSOptions) {}

  connect() {
    this.stopped = false;
    this.open();
  }

  private setStatus(s: ConnStatus) {
    if (this.status === s) return;
    this.status = s;
    this.opts.onStatus?.(s);
  }

  private open() {
    if (this.stopped) return;
    this.setStatus(this.lastSeq > 0 || this.ws !== null ? "reconnecting" : "connecting");
    const params = new URLSearchParams({ role: this.opts.role, code: this.opts.code });
    if (this.opts.token) params.set("token", this.opts.token);
    const proto = location.protocol === "https:" ? "wss" : "ws";
    const url = `${proto}://${location.host}/ws?${params}`;

    const ws = new WebSocket(url);
    this.ws = ws;

    ws.onopen = () => {
      this.setStatus("open");
      this.backoff = INIT_BACKOFF;
      this.sendNow("hello", {
        protocol: PROTOCOL_VERSION,
        lastSeq: this.lastSeq || undefined,
        clientTime: Date.now(),
      } satisfies HelloData);
      this.startPing();
      // envia fila (respostas pendentes)
      const q = this.queue;
      this.queue = [];
      for (const m of q) ws.send(JSON.stringify({ t: m.t, d: m.d }));
    };

    ws.onmessage = (ev) => {
      let msg: Envelope;
      try {
        msg = JSON.parse(ev.data as string) as Envelope;
      } catch {
        return;
      }
      if (msg.seq && msg.seq > this.lastSeq) this.lastSeq = msg.seq;
      if (msg.t === "pong") {
        const d = msg.d as { serverTime: number; clientTime: number };
        const now = Date.now();
        const rtt = now - d.clientTime;
        this.clockOffset = d.serverTime + rtt / 2 - now;
        return;
      }
      this.opts.onMessage(msg.t as ServerType, msg.d, msg.seq);
    };

    ws.onclose = () => {
      this.stopPing();
      if (this.stopped) return;
      this.setStatus("reconnecting");
      const delay = this.backoff + Math.random() * this.backoff * 0.3;
      this.backoff = Math.min(this.backoff * 2, MAX_BACKOFF);
      setTimeout(() => this.open(), delay);
    };

    ws.onerror = () => {
      ws.close();
    };
  }

  private startPing() {
    this.stopPing();
    this.pingTimer = setInterval(() => {
      this.sendNow("ping", { clientTime: Date.now() });
    }, PING_INTERVAL);
  }

  private stopPing() {
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.pingTimer = null;
  }

  /** offset em ms para converter epoch do servidor em Date.now() local. */
  serverNow(): number {
    return Date.now() + this.clockOffset;
  }

  /** Envia mensagem; se offline, enfileira apenas `answer`. */
  send(t: ClientType, d: unknown) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.sendNow(t, d);
      return;
    }
    if (t === "answer") {
      this.queue = this.queue.filter((m) => m.t !== "answer");
      this.queue.push({ t, d });
    }
  }

  private sendNow(t: ClientType, d: unknown) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ t, d }));
    }
  }

  close() {
    this.stopped = true;
    this.stopPing();
    if (this.noticeTimer) clearTimeout(this.noticeTimer);
    this.ws?.close();
    this.ws = null;
    this.setStatus("closed");
  }
}
